<template>
  <div class="local-fulfillment">
    <h1>{{ msg }}</h1>

    <p>
    空間知能化のお勉強をしましょう。
    </p>

<h2>
Local fulfillment
</h2>

<h4>
もくじ
</h4>

<div class="talka">
<ol>
<li>
<a href="#1">Local fulfillmentの概要</a>
</li>
<li>
<a href="#2">smarthome.DeviceManagerクラス</a>
</li>
<li>
<a href="#3">smarthome.Appクラス</a>
</li>
<li>
<a href="#4">Local Fulfillmentアプリの例</a>
</li>
<li>
<a href="#5">実装パス</a>
</li>
</ol>

</div>

<h3 id="1">
1.Local fulfillmentの概要
</h3>

<div class="talka">

<h4>
Local fulfillmentの概要
</h4>

<ul>
<li>
Local Home SDKは、スマートホームインテントをルーティングするためのローカルフルフィルメントパスを追加することにより、Googleアシスタントとのスマートホームの統合を強化します 。
</li>
<li>
SDKでは、TypeScriptまたはJavaScriptを使用して、スマートホームビジネスロジックを含むローカルフルフィルメントアプリを作成できます。
<br>Google HomeまたはGoogle Nestデバイスは、デバイス上でアプリをロードして実行できます。
<br>アプリは、ローカルエリアネットワーク（LAN）上のWi-Fiを介して既存のスマートデバイスと直接通信し、既存のプロトコルを介してユーザーコマンドを実行します。
</li>
<li>
SDKの統合により、スマートホームアクションのパフォーマンスが向上します。
<br>ローカルフルフィルメントは、2要素認証を使用するものを除き、すべてのデバイスタイプとデバイストレイトでサポートされています。
</li>
</ul>

<ol>
<li>
<a href="https://developers.google.com/assistant/smarthome/develop/local">ビルドを開始する</a>
</li>
<li>
<a href="https://github.com/actions-on-google/smart-home-local">サンプルを試す</a>
</li>
</ol>

<h4>
仕組みを理解する
</h4>

<ul>
<li>
あなたのクラウドアプリからのSYNC応答を取得した後、ローカルホームプラットフォームはのmDNSを使用して、ユーザーのローカル・エリア・ネットワークをスキャンし、UDPブロードキャスト、またはUPnPはアシスタントに接続されたスマートデバイスを検出します。
</li>
<li>
プラットフォームIDENTIFYは、IDENTIFY応答内のデバイスIDを以前のSYNC応答によって返されたものと比較することにより、デバイスがローカルで制御可能かどうかを判断するインテントを送信します。
</li>
<li>
検出されたデバイスがハブまたはブリッジである場合、プラットフォームはREACHABLE_DEVICESインテントを送信し、ローカルで通信するためのプロキシデバイスとしてハブを扱います。
</li>
<li>
ローカルデバイスを確認する応答を受信すると、プラットフォームはユーザーのGoogle HomeまたはGoogle Nestデバイスへのローカルフルフィルメントパスを確立し、
続いてローカルフルフィルメントのためにユーザーコマンドをルーティングします。
</li>
<li>
ユーザーがローカルフルフィルメントパスを持つスマートホームアクションをトリガーすると、アシスタントはEXECUTE インテントをクラウドフルフィルメントではなく、
Google HomeまたはGoogle Nestデバイスに送信します。次に、デバイスはローカルのフルフィルメントアプリを実行してインテントを処理します。
</li>
</ul>

<figure style="max-width: 800px;">
<img src="http://www.hiroict.com/img/akiko/local-home-sdk-execute-path.png" width="100%">
<figcaption align="center">図2</figcaption>
</figure>

</div>

<h3 id="2">
2.smarthome.DeviceManagerクラス
</h3>

<p>
「<a href="https://developers.google.com/assistant/smarthome/reference/local/interfaces/smarthome.devicemanager">smarthome.DeviceManager</a>」から引用。
</p>
<p>
smarthome.DeviceManagerクラスは、デバイスにアクセスする通信機能を提供します。
</p>

<div class="talka">

<h4>
smarthome.DeviceManagerクラスの概要
</h4>

<ul>
<li>
このクラスは、ローカルホームプラットフォームによって管理されるデバイスへのアクセスを提供します。 
</li>
<li>
アプリケーションはこのインターフェースを使用して、ローカルで識別されたデバイスとsendメソッドを使用してコマンドを送受信できます。
</li>
</ul>

<pre>
localHomeApp.getDeviceManager()
  .send(deviceCommand)
  .then((result) => {
    // Handle command success
  })
  .catch((err: IntentFlow.HandlerError) => {
    // Handle command error
  });
</pre>

<h4>
メソッドの種類
</h4>

<ul>
<li>
以下のメソッドの種類があります。
</li>
</ul>

<pre>
1.getProxyInfo(id: string): ProxyInfo
2.markPending(request: IntentRequest): Promise&lt;void&gt;
3.send(command: CommandRequest): Promise&lt;CommandSuccess&gt;
</pre>

<h4>
getProxyInfo()メソッド
</h4>

<pre>
getProxyInfo(id: string): ProxyInfo
</pre>

<ul>
<li>
このエンドデバイスを制御するハブ/ブリッジに関する情報を取得するためにアプリによって呼び出されます。
</li>
</ul>

<pre>
●Parameters
id: string
→Device ID of end device that is being controlled by the hub.

●Returns ProxyInfo
</pre>

<h4>
markPending()メソッド
</h4>

<pre>
markPending(request: IntentRequest): Promise&lt;void&gt;
</pre>

<ul>
<li>
アプリがインテントの処理を完了すると、markPendingがアプリによって呼び出されますが、実際の操作（通常はEXECUTEコマンド）はまだ実行されていません。
</li>
<li>
これにより、Google Homeはユーザーにタイムリーに応答できます。 
</li>
<li>
これは、長時間実行する操作に役立ちます。 約束を返します。
</li>
</ul>

<pre>
●Parameters
request: IntentRequest
→Original intent request that should be marked pending.

●Returns Promise&lt;void&gt;
</pre>

<h4>
send()メソッド
</h4>

<pre>
send(command: CommandRequest): Promise&lt;CommandSuccess&gt;
</pre>

<ul>
<li>
send()メソッドは、デバイスと通信する必要があるときにアプリによって呼び出されます。 
</li>
<li>
デバイスで使用されるプロトコルに応じて、アプリはDataFlow.CommandRequestオブジェクトを作成し、それを引数として渡します。
</li>
<li>
DataFlow.CommandSuccessに解決されるpromiseを返します。 
</li>
<li>
読み取り要求の場合、応答はデータを返す場合があります。
</li>
</ul>

<pre>
●Parameters
command: CommandRequest
→Command to communicate with the device.

●Returns Promise&lt;CommandSuccess&gt;
Promise that resolves to DataFlow.CommandSuccess
</pre>


</div>

<h3 id="3">
3.smarthome.Appクラス
</h3>

<p>
「<a href="https://developers.google.com/assistant/smarthome/reference/local/classes/smarthome.app">smarthome.App</a>」から引用。
</p>
<p>
smarthome.Appクラスは、「Local Home」アプリのイベントを制御する機能を提供します。
</p>

<div class="talka">

<h4>
smarthome.Appの概要
</h4>

<ul>
<li>
このクラスは、ローカルホームプラットフォームでのアプリのメインエントリポイントです。 
</li>
<li>
Appクラスを使用して、インテントのコールバックハンドラーを登録し、新しいイベントをリッスンします。
</li>
</ul>

  
<pre>
import App = smarthome.App;

const localHomeApp: App = new App("1.0.0");
localHomeApp
  .onIdentify(identifyHandler)
  .onExecute(executeHandler)
  .listen()
  .then(() => console.log("Ready"));
</pre>

<h4>
メソッドの種類
</h4>

<ul>
<li>
以下のメソッドの種類があります。
</li>
</ul>

<pre>
1.getDeviceManager(): DeviceManager
2.onExecute(handler: ExecuteHandler): this
3.onIdentify(handler: IdentifyHandler): this
4.onReachableDevices(handler: ReachableDevicesHandler): this
</pre>

<h4>
getDeviceManager()メソッド
</h4>

<pre>
getDeviceManager(): DeviceManager
</pre>

<ul>
<li>
getDeviceManager()メソッドは、シングルトンDeviceManagerオブジェクトへの参照を取得するためにアプリによって呼び出されます。
</li>
</ul>

<pre>
●Returns DeviceManager
DeviceManager
</pre>


</div>


<h3 id="4">
4.Local Fulfillmentアプリの例
</h3>

<p>
「<a href="https://vstone.co.jp/robotshop/blog/archives/950">Nest MiniでLocal Home SDKを使ってお家のIoTロボットを操作する 第3回</a>」から引用。
</p>

<div class="talka">

<h4>
Local Fulfillmentアプリの例
</h4>

<ul>
<li>
<a href="https://github.com/actions-on-google/local-home-sdk/blob/master/CHANGELOG.md">変更ログ（local-home-sdk /CHANGELOG.md）</a>を参照して下さい。
</li>
</ul>

<h4>
/local/index.ts
</h4>

<pre>
/**
 * Copyright 2019, Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/// <reference types="@google/local-home-sdk" />

import App = smarthome.App;
import Constants = smarthome.Constants;
import DataFlow = smarthome.DataFlow;
import Execute = smarthome.Execute;
import Intents = smarthome.Intents;
import IntentFlow = smarthome.IntentFlow;

// サーバのポート番号
const SERVER_PORT = 3388;

// IWasherParamsの型を宣言
interface IWasherParams {
  on?: boolean,
  start?: boolean,
  pause?: boolean,
}

// LocalExecutionAppクラスの定義
class LocalExecutionApp {

  constructor(private readonly app: App) { }

  // IDENTIFYハンドラを定義する
  identifyHandler(request: IntentFlow.IdentifyRequest):
      Promise&lt;IntentFlow.IdentifyResponse&gt; {
    // TODO: Implement device identification
    // IDENTIFYハンドラを記述
    console.log("IDENTIFY intent: " + JSON.stringify(request, null, 2));

    const scanData = request.inputs[0].payload.device.udpScanData;
    if (!scanData) {
      const err = new IntentFlow.HandlerError(request.requestId, 'invalid_request', 'Invalid scan data');
      return Promise.reject(err);
    }

    // In this codelab, the scan data contains only local device id.
    const localDeviceId = Buffer.from(scanData.data, 'hex');

    const response: IntentFlow.IdentifyResponse = {
      intent: Intents.IDENTIFY,
      requestId: request.requestId,
      payload: {
        device: {
          id: 'washer',
          verificationId: localDeviceId.toString(),
        }
      }
    };
    console.log("IDENTIFY response: " + JSON.stringify(response, null, 2));

    return Promise.resolve(response);
  }

  // EXECUTEハンドラを定義する
  executeHandler(request: IntentFlow.ExecuteRequest):
      Promise&lt;IntentFlow.ExecuteResponse&gt; {
    // TODO: Implement local execution
    // EXECUTEハンドラを記述
    console.log("EXECUTE intent: " + JSON.stringify(request, null, 2));

    const command = request.inputs[0].payload.commands[0];
    const execution = command.execution[0];
    const response = new Execute.Response.Builder()
      .setRequestId(request.requestId);

    const promises: Array&lt;Promise&lt;void&gt;&gt; = command.devices.map((device) =&gt; {
      console.log("Handling EXECUTE intent for device: " + JSON.stringify(device));

      // Convert execution params to a string for the local device
      const params = execution.params as IWasherParams;
      const payload = this.getDataForCommand(execution.command, params);

      // Create a command to send over the local network
      const radioCommand = new DataFlow.HttpRequestData();
      radioCommand.requestId = request.requestId;
      radioCommand.deviceId = device.id;
      radioCommand.data = JSON.stringify(payload);
      radioCommand.dataType = 'application/json';
      radioCommand.port = SERVER_PORT;
      radioCommand.method = Constants.HttpOperation.POST;
      radioCommand.isSecure = false;

      console.log("Sending HTTP request to the smart home device:", payload);
      // コマンドをデバイスに送信する
      return this.app.getDeviceManager()
        .send(radioCommand)
        .then(() => {
          const state = {online: true};
          response.setSuccessState(device.id, Object.assign(state, params));
          console.log(`Command successfully sent to ${device.id}`);
        })
        .catch((e: IntentFlow.HandlerError) => {
          e.errorCode = e.errorCode || 'invalid_request';
          response.setErrorState(device.id, e.errorCode);
          console.error('An error occurred sending the command', e.errorCode);
        });
    });

    // 処理を終了するとPromiseを返す
    return Promise.all(promises)
      .then(() => {
        return response.build();
      })
      .catch((e) => {
        const err = new IntentFlow.HandlerError(request.requestId, 'invalid_request', e.message);
        return Promise.reject(err);
      });
  }

  /**
   * Convert execution request into a local device command
   */
  // commandの型：string
  // paramの型：IWasherParams 
  getDataForCommand(command: string, params: IWasherParams): unknown {
    switch (command) {
      case 'action.devices.commands.OnOff':
        return {
          on: params.on ? true : false
        };
      case 'action.devices.commands.StartStop':
        return {
          isRunning: params.start ? true : false
        };
      case 'action.devices.commands.PauseUnpause':
        return {
          isPaused: params.pause ? true : false
        };
      default:
        console.error('Unknown command', command);
        return {};
    }
  }
} // LocalExecutionAppクラスの定義（終り）

// localHomeSdkクラスを生成する
const localHomeSdk = new App('1.0.0');
// LocalExecutionAppクラスを生成する
const localApp = new LocalExecutionApp(localHomeSdk);
// localHomeSdkクラスを実行する
localHomeSdk
  .onIdentify(localApp.identifyHandler.bind(localApp))
  .onExecute(localApp.executeHandler.bind(localApp))
  .listen()
  .then(() => console.log('Ready'))
  .catch((e: Error) => console.error(e));
</pre>

</div>


<h3 id="5">
5.実装パス
</h3>

<div class="talka">

<h4>
ローカルホームSDKをスマートホーム統合に利用するには、次のタスクを実行する必要があります。
</h4>

<ol>
<li>
スキャン構成をセットアップする
</li>
<ul>
<li>
アシスタントがローカルで制御可能なデバイスを検出するために必要なパラメーターを使用して、アクションコンソールを構成します。
</li>
</ul>
<li>
クラウドフルフィルメントのSYNC応答を更新する
</li>
<ul>
<li>
クラウドフルフィルメントで、SYNC リクエストハンドラーを変更し otherDeviceIdsて、プラットフォームがローカルフルフィルメントパスを確立するために使用するフィールドをサポートします。
</li>
<li>
そのフィールドで、ローカルで制御できるデバイスのIDを指定します。
</li>
</ul>
<li>
ローカルフルフィルメントアプリを実装する
</li>
<ul>
<li>
Local Home SDKを使用して、IDENTIFYおよび EXECUTEインテントを処理するJavaScriptアプリを作成します。
</li>
<li>
ハブまたはブリッジプロキシデバイスの場合、REACHABLE_DEVICESインテントも処理する必要があります。
</li>
</ul>
<li>
アプリをテストしてデバッグする
</li>
<ul>
<li>
スマートホームのテストスイートを使用して、統合をテスト（または自己認証）します。
</li>
</ul>
</ol>

<p>
ローカルフルフィルメントの使用を開始するには、<a href="https://developers.google.com/assistant/smarthome/develop/local">スマートホームアクションにローカルフルフィルメントを追加する</a>をご覧 ください。
</p>

</div>



  </div> <!-- end of talka -->

</template>

<script>
import Vue from 'vue'
import VueYoutube from 'vue-youtube'

Vue.use(VueYoutube)

export default {
  name: 'DanceWorld',
  props: {
    msg: String
  },
  data() {
    return {
      //album_index: 0,
      //album_title : 'ワルツ',
      //video_index: 0,
      //video_title : '01 いきいきストレッチング',

      //video_index_1: 0,
      //video_id: '5IlfzLYadNk',

      //albums: [],
    }
  },
  created () {
    this.runAll();
    //this.albums = [this.album_1, this.album_2, this.album_3, this.album_4, this.album_5, this.album_6, this.album_7]
    //this.firstVideo()
  },
  methods: {
    async runAll(){
      try{
        console.log("[START]");
        const res1 = await this.f1();
        console.log(res1);

        const res2 = await this.f2(res1);
        console.log(res2);

        const res3 = await this.f3(res2);
        console.log(res3);

        const res4 = await this.f4(res3);
        console.log("Final function: " + res4);

        console.log("[END]");

      }catch(err){
        //エラー処理
        //とりあえず何もしない
      }
    },
    //setTimeoutをいちいち書くのが面倒なので関数化
    Timeout(passVal, ms) {
      return new Promise(resolve =>
        setTimeout(() => {
            resolve(passVal);
        }, ms)
      )
    },
    async f1(){
      console.log("#1: f1");
      return "f1 ==> f2";
    },
    async f2(passVal){
      console.log("#2: f2 passVal: " + passVal);
      return this.Timeout("f2 ==> f3", Math.random()*2000);
    },
    async f3(passVal){
      console.log("#3: f3 passVal: " + passVal);
      return this.Timeout("f3 ==> f4", Math.random()*3000);
    },
    async f4(passVal){
        console.log("#4: f4 passVal: " + passVal);
        return this.Timeout("f4", Math.random()*3000);
    },
    /*
    selectAlbum(index) {
      window.console.log("selectAlbum() 1.index: " + index);
      this.album_index = index;
      this.firstVideo()
    },
    */
  }
  
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.local-fulfillment {
  //margin-left: 20px;
}

.talka {
  margin-left: 10px;
}

pre {
  margin-left: 10px;
  background-color: #ffffaa;
}

h3 {
  margin: 40px 0 0;
}
ol {
  //list-style-type: none;
  padding: 0;
  margin-left: 20px;
}
ul {
  //list-style-type: none;
  padding: 0;
  margin-left: 20px;
}
li {
  //display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
</style>
